import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import "./system.css";

const HomeSystem = () => {
  const containerRef = useRef();
  const textRef = useRef();
  const subTextRef = useRef();
  const buttonRef = useRef();
  const gridRef = useRef();

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power4.out" } });

      // Cinematic Entrance
      tl.fromTo(".mesh-bg", { scale: 1.2, opacity: 0 }, { scale: 1, opacity: 1, duration: 2 })
        .from(textRef.current, { y: 100, opacity: 0, duration: 1.2 }, "-=1")
        .from(subTextRef.current, { y: 50, opacity: 0, duration: 1 }, "-=0.8")
        .from(buttonRef.current, { scale: 0.8, opacity: 0, duration: 0.8 }, "-=0.5")
        .to(".grid-line", { opacity: 0.15, duration: 2, stagger: 0.05 }, "-=1");

      // Floating particles motion
      gsap.to(".floating-blob", {
        y: "random(-40, 40)",
        x: "random(-40, 40)",
        duration: 5,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        stagger: 1
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <section 
      ref={containerRef}
      className="relative w-full h-screen flex flex-col justify-center items-center text-center overflow-hidden bg-[#030014]"
    >
      {/* Dynamic Mesh Background */}
      <div className="mesh-bg absolute inset-0 z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-indigo-600/20 blur-[120px] floating-blob"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-purple-600/20 blur-[120px] floating-blob"></div>
        <div className="absolute top-[20%] right-[10%] w-[30%] h-[30%] rounded-full bg-blue-500/10 blur-[100px] floating-blob"></div>
      </div>

      {/* Subtle Architectural Grid */}
      <div className="absolute inset-0 z-[1] opacity-0 grid-line" style={{ backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)`, backgroundSize: '60px 60px' }}></div>

      <div className="relative z-10 max-w-5xl px-6">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 text-blue-400 text-xs font-bold tracking-widest uppercase mb-8 backdrop-blur-md">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
          </span>
          Next Gen Event Management
        </div>

        {/* Main Title */}
        <h1 
          ref={textRef}
          className="text-6xl md:text-8xl lg:text-9xl font-black text-white mb-8 tracking-tighter leading-[0.9]"
        >
          Elevating <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-500">
            Experiences
          </span>
        </h1>

        {/* Subtitle */}
        <p 
          ref={subTextRef}
          className="max-w-2xl mx-auto text-gray-400 text-lg md:text-xl font-light leading-relaxed mb-12"
        >
          My Color Events & Design blends <span className="text-white font-medium">artistry with precision</span> to curate extraordinary moments that resonate forever.
        </p>

        {/* CTA Button */}
        <div ref={buttonRef}>
          <a
            href="http://mycolorevent.com/"
            target="_blank"
            className="group relative inline-flex items-center justify-center px-10 py-4 font-bold text-white transition-all duration-300 bg-blue-600 rounded-full hover:bg-blue-700 shadow-[0_0_30px_rgba(37,99,235,0.3)] hover:shadow-[0_0_50px_rgba(37,99,235,0.5)]"
          >
            <span className="relative flex items-center gap-2">
              Explore Portfolio
              <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </span>
          </a>
        </div>
      </div>

      {/* Mouse Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-30">
        <div className="w-[1px] h-12 bg-gradient-to-b from-blue-500 to-transparent"></div>
        <span className="text-[10px] text-white uppercase tracking-[0.3em]">Scroll</span>
      </div>
    </section>
  );
};

export default HomeSystem;